import {
    Building2, Users as UsersIcon, Bot, TrendingUp, UserCircle, CalendarClock,
    Briefcase, MapPin, Flame, DollarSign, Compass, Calculator, Send, UserCog,
    LayoutDashboard, Activity, PhoneCall, Database, Filter, Home, ClipboardList,
    ClipboardCheck, Tag, Crown, LineChart, Target, Facebook,
} from 'lucide-react';

/**
 * Every Analytics tab, in one place.
 *
 * Analytics used to be a single 14-tab strip mixing two unrelated products:
 * how CONSUMERS use the buyer portal, and how the AGENCY uses the CRM. They
 * answer different questions for different people, so they are now split into
 * two environments (Portal / CRM) selected above the tabs.
 *
 *   env      'executive' | 'portal' | 'crm'  — which environment the tab is in
 *            ('crm' is the environment shown to users as Agent Analytics,
 *            renamed 11 Sep 2026, with the id deliberately left alone)
 *   adminOnly                  — hidden from plain agents
 *   superAdminOnly             — hidden from plain ADMINS too; only the agency
 *                                principal (super admin), Django staff and devs
 *                                see it. Set on every CRM and Executive tab,
 *                                and on their two environments below, after
 *                                Christian asked on 31 Aug 2026 to "Cut CRM
 *                                analytics & Executive from other admins".
 *                                It is a display rule only — the backend gates
 *                                the same endpoints on core.permissions
 *                                .IsSuperAdmin, because a hidden tab is not a
 *                                permission.
 *   section                    — the CRM Analytics section slug this tab renders
 *                                through the shared CrmSectionPanel; tabs
 *                                without one have a bespoke panel
 *   title / subtitle           — the page heading for that tab
 *
 * Driving the tab strip AND the heading from this one array is deliberate: they
 * used to be three hand-maintained lists (buttons, a title ternary, a subtitle
 * ternary) and they had already drifted — the Heatmap tab rendered under the
 * heading "GeeVee Analytics" because the ternary had no branch for it.
 *
 * Adding a tab = adding an entry here plus a render branch in AnalyticsPage.
 *
 * ONE RULE, added 16 Aug 2026 after the client reported Executive and CRM
 * Analytics as "very similar": a panel appears on exactly ONE environment. If
 * two tabs would render the same thing, the tab is deleted rather than
 * duplicated, and the environment that owns it is the one whose question it
 * answers. Pinned by a test in crmAnalytics.test.js.
 */
export const ANALYTICS_TABS = [
    // ---------------------------------------------------------------- CRM --
    {
        id: 'crm_overview',
        env: 'crm',
        label: 'Overview',
        icon: LayoutDashboard,
        adminOnly: true,
        superAdminOnly: true,
        title: 'Agent Analytics Overview',
        subtitle: 'The management cockpit • Who is using the CRM, what they are doing in it, '
            + 'and whether that activity is turning into appraisals, listings and commission',
    },
    {
        id: 'agents',
        env: 'crm',
        label: 'Agents',
        icon: UserCog,
        adminOnly: true,
        superAdminOnly: true,
        title: 'Agents',
        subtitle: 'Per-agent performance • Current listings, listings signed, withdrawn, appraisals, '
            + 'days on market, average commission, listing sources and the selling-lead funnel',
    },
    {
        id: 'agent_usage',
        env: 'crm',
        label: 'Agent Usage',
        icon: Activity,
        adminOnly: true,
        superAdminOnly: true,
        title: 'Agent Usage',
        subtitle: 'CRM utilisation • Active days, modules used, database coverage and a '
            + 'utilisation score out of 100 for every agent, with the feature-adoption matrix',
    },
    {
        id: 'crm_prospecting',
        env: 'crm',
        label: 'Prospecting',
        icon: PhoneCall,
        adminOnly: true,
        superAdminOnly: true,
        section: 'prospecting',
        title: 'Prospecting',
        subtitle: 'Outbound activity • Calls, SMS, emails and notes logged, unique people '
            + 'reached and touches per working day',
    },
    {
        id: 'crm_database',
        env: 'crm',
        label: 'Database',
        icon: Database,
        adminOnly: true,
        superAdminOnly: true,
        section: 'database',
        title: 'Database',
        subtitle: 'The agency asset • Book size, coverage, records added and updated, '
            + 'contacts going stale and callback discipline',
    },
    {
        id: 'crm_leads',
        env: 'crm',
        label: 'Leads',
        icon: Filter,
        adminOnly: true,
        superAdminOnly: true,
        section: 'leads',
        title: 'Lead Management',
        subtitle: 'Volume, speed and movement • Response times, never-actioned leads, '
            + 'aging pipeline and stage movement',
    },
    {
        id: 'crm_buyers',
        env: 'crm',
        label: 'Buyers',
        icon: UsersIcon,
        adminOnly: true,
        superAdminOnly: true,
        section: 'buyers',
        title: 'Buyers',
        subtitle: 'What agents do with buyers • Enquiries, temperature, contract requests, '
            + 'offers and buyers who have a property to sell',
    },
    {
        id: 'crm_inspections',
        env: 'crm',
        label: 'Inspections',
        icon: Home,
        adminOnly: true,
        superAdminOnly: true,
        section: 'inspections',
        title: 'Inspections',
        subtitle: 'Open homes • Attendance, new buyers captured, follow-up rate, hot buyers '
            + 'and seller leads found at the door',
    },
    {
        id: 'crm_tasks',
        env: 'crm',
        label: 'Tasks',
        icon: ClipboardList,
        adminOnly: true,
        superAdminOnly: true,
        section: 'tasks',
        title: 'Tasks & Follow-Up',
        subtitle: 'Operating discipline • Tasks and callbacks created, completed, overdue, '
            + 'and a follow-through score for every agent',
    },
    {
        id: 'communications',
        env: 'crm',
        label: 'Communications',
        icon: Send,
        adminOnly: true,
        superAdminOnly: true,
        title: 'Communications',
        subtitle: 'Email and SMS campaign performance • delivered, open, click, bounce and '
            + 'unsubscribe rates, plus your top campaigns',
    },
    {
        id: 'campaign_leads',
        env: 'crm',
        label: 'Campaign Leads',
        icon: Target,
        adminOnly: true,
        superAdminOnly: true,
        title: 'Campaign Leads',
        subtitle: 'What the email and SMS actually produced • Every campaign link is tagged, '
            + 'so the portal visits, sign-ups and enquiries that follow are credited back '
            + 'to the campaign that caused them',
    },
    {
        id: 'facebook_leads',
        env: 'crm',
        label: 'Facebook Leads',
        icon: Facebook,
        adminOnly: true,
        superAdminOnly: true,
        title: 'Facebook Leads',
        subtitle: 'Paid social intake • Every Facebook and Instagram lead-ad enquiry as it '
            + 'lands in the CRM, which form and campaign produced it, who it was assigned '
            + 'to, and whether the feed is still alive',
    },
    {
        id: 'crm_appraisals',
        env: 'crm',
        label: 'Appraisals',
        icon: ClipboardCheck,
        adminOnly: true,
        superAdminOnly: true,
        section: 'appraisals',
        title: 'Appraisals',
        subtitle: 'The vendor opportunity • Appraisals taken, what is still in the pipeline, '
            + 'conversion to a listing and how long it takes',
    },
    {
        id: 'crm_listings',
        env: 'crm',
        label: 'Listings',
        icon: Tag,
        adminOnly: true,
        superAdminOnly: true,
        section: 'listings',
        title: 'Listings',
        subtitle: 'Stock won, lost and carried • On and off market, withdrawn, win rate, '
            + 'days on market and where the listings came from',
    },
    {
        id: 'crm_revenue',
        env: 'crm',
        label: 'Revenue',
        icon: DollarSign,
        adminOnly: true,
        superAdminOnly: true,
        section: 'revenue',
        title: 'Sales & Revenue',
        subtitle: 'The end of the funnel • Commission per listing, per appraisal, per lead '
            + 'and per contact touched, and which CRM behaviour produced it',
    },
    {
        id: 'crm_conversion',
        env: 'crm',
        label: 'Conversion',
        icon: LineChart,
        adminOnly: true,
        superAdminOnly: true,
        section: 'conversion',
        title: 'Conversion',
        subtitle: 'Contacts through to commission • The full funnel and the drop-off at '
            + 'every step, per agent',
    },

    // ---------------------------------------------------------- EXECUTIVE --
    /*
     * Executive is the ALTITUDE above CRM Analytics, not a second copy of it.
     *
     * It used to carry six tabs, four of which re-pointed at a panel that was
     * already on another environment: Revenue was CRM > Revenue, Funnel was
     * CRM > Conversion, Agents was CRM > Agents, and Marketing was Portal >
     * Cost Per Lead. Christian, 16 Aug 2026: "Executive & CRM Analytics seem
     * very similar... remove duplicates... i am struggling to read the data &
     * understand whats different."
     *
     * Executive now holds only what is genuinely agency-level and exists
     * nowhere else: the scoreboard, and the movement against last period.
     * Every per-agent breakdown lives on CRM Analytics, once each.
     */
    {
        id: 'exec_overview',
        env: 'executive',
        label: 'Overview',
        icon: Crown,
        adminOnly: true,
        superAdminOnly: true,
        section: 'exec_overview',
        title: 'Executive Overview',
        subtitle: 'The agency at a glance • Every headline number with the previous '
            + 'period beside it, and the scoreboard of who produced what',
    },
    {
        id: 'exec_growth',
        env: 'executive',
        label: 'Growth',
        icon: TrendingUp,
        adminOnly: true,
        superAdminOnly: true,
        section: 'exec_growth',
        title: 'Growth',
        subtitle: 'Period on period • Every headline number against the equally long '
            + 'period before it',
    },

    // ------------------------------------------------------------- PORTAL --
    {
        id: 'daily',
        env: 'portal',
        label: 'Report',
        icon: CalendarClock,
        adminOnly: true,
        title: 'Report',
        subtitle: 'Auto-generated each day • Portal overview, revenue attribution, buyer journey, '
            + 'and what to improve',
    },
    {
        id: 'property',
        env: 'portal',
        label: 'Property Analytics',
        icon: Building2,
        title: 'Property Analytics',
        subtitle: 'Top performing properties • Listings with the most buyer interactions',
    },
    {
        id: 'buyer',
        env: 'portal',
        label: 'Buyer Analytics',
        icon: UsersIcon,
        title: 'Buyer Analytics',
        subtitle: 'Sourcing checklist & buyer demand • Priority suburbs, budgets, finance readiness, '
            + 'preferences and trends',
    },
    {
        id: 'renter',
        env: 'portal',
        label: 'Renter Analytics',
        icon: UsersIcon,
        title: 'Renter Analytics',
        subtitle: 'Renter demand • Priority suburbs, property types, bedrooms and rent preferences',
    },
    {
        id: 'engagement',
        env: 'portal',
        label: 'Engagement',
        icon: TrendingUp,
        adminOnly: true,
        title: 'Engagement',
        subtitle: 'Retention & journey • Returning users, days between visits, lead source, GeeVee topics',
    },
    {
        id: 'chatbot',
        env: 'portal',
        label: 'GeeVee',
        icon: Bot,
        adminOnly: true,
        title: 'GeeVee Analytics',
        subtitle: 'GeeVee AI assistant • Sessions, property preferences, question categories, '
            + 'and conversation quality',
    },
    {
        id: 'profiles',
        env: 'portal',
        label: 'User Profiles',
        icon: UserCircle,
        adminOnly: true,
        title: 'User Profiles',
        subtitle: 'Unified profile segments • Landlord/investor, finance, advocacy, saves, '
            + 'calculator & chat usage',
    },
    {
        id: 'portfolio',
        env: 'portal',
        label: 'Investor Analytics',
        icon: Briefcase,
        adminOnly: true,
        title: 'Investor Analytics',
        subtitle: 'Investor CRM • Platform overview → investor table → individual investor profile. '
            + 'Value, equity, cashflow, behaviour and opportunities across every investor',
    },
    {
        id: 'location',
        env: 'portal',
        label: 'Location',
        icon: MapPin,
        adminOnly: true,
        title: 'Location',
        subtitle: 'Where buyers are based • Country, state and city breakdown derived from IP, '
            + 'with an interactive map',
    },
    {
        id: 'heatmap',
        env: 'portal',
        label: 'Heatmap',
        icon: Flame,
        adminOnly: true,
        title: 'Heatmap',
        subtitle: 'Click and scroll behaviour • Where buyers actually interact on each page of the portal',
    },
    {
        id: 'cost_per_lead',
        env: 'portal',
        label: 'Cost Per Lead',
        icon: DollarSign,
        adminOnly: true,
        title: 'Cost Per Lead',
        subtitle: 'Acquisition economics • Ad spend matched to signups, activations and enquiries '
            + 'by campaign — CAC, funnel and ROAS',
    },
    {
        id: 'suburb_research',
        env: 'portal',
        label: 'Suburb Research',
        icon: Compass,
        adminOnly: true,
        title: 'Suburb Research',
        subtitle: 'Suburb intelligence demand • Which suburbs buyers research, what they search for, '
            + 'and daily activity on the Research page',
    },
    {
        id: 'calculators',
        env: 'portal',
        label: 'Calculators',
        icon: Calculator,
        adminOnly: true,
        title: 'Calculators',
        subtitle: 'All portal calculators in one place • Investor Tools (14 calculators), '
            + 'Mortgage and SMSF usage, results and leads',
    },
];

export const ENVIRONMENTS = [
    {
        id: 'portal',
        label: 'Portal Analytics',
        description: 'Monitor buyer engagement and platform performance',
    },
    {
        // Shown as "Agent Analytics" since 11 Sep 2026, on Christian's ask:
        // "can we please change the name of crm analytics to agent analytics".
        // The id stays 'crm'. It is in saved ?view= links, the localStorage
        // key, the backend section slugs and every cache key, and none of that
        // is what he was renaming.
        id: 'crm',
        label: 'Agent Analytics',
        description: 'How the team uses the CRM, agent by agent, and how that activity becomes revenue',
        adminOnly: true,
        superAdminOnly: true,
    },
    // Sits to the RIGHT of Agent Analytics on Christian's instruction (16 Aug
    // 2026): you read the detail first, then step up to the summary.
    {
        id: 'executive',
        label: 'Executive',
        description: 'The agency total and how it moved against last period. The per-agent detail behind it is on Agent Analytics',
        adminOnly: true,
        superAdminOnly: true,
    },
];

/** The CRM Analytics section a tab renders, or null for a bespoke panel. */
export const sectionForTab = (id) => tabById(id)?.section || null;

export const tabById = (id) => ANALYTICS_TABS.find((t) => t.id === id);

/** The environment a tab belongs to — used to resolve a `?view=` deep link. */
export const envForTab = (id) => tabById(id)?.env;

/**
 * The tabs a viewer may see in an environment.
 *
 * Two independent gates, checked in tier order:
 *   isAgent      — a plain agent, so `adminOnly` tabs drop out
 *   isSuperAdmin — false for a PLAIN ADMIN, so `superAdminOnly` tabs drop out
 *
 * `isSuperAdmin` defaults to true when the caller does not pass it. Every
 * caller in the app does pass it; the default keeps a bare `tabsFor('portal')`
 * (and the older tests) from silently returning an empty CRM strip and looking
 * like the data broke.
 */
export const tabsFor = (env, { isAgent, isSuperAdmin = true } = {}) =>
    ANALYTICS_TABS.filter(
        (t) =>
            t.env === env
            && !(t.adminOnly && isAgent)
            && !(t.superAdminOnly && !isSuperAdmin)
    );

/** The environments a viewer may select, same two gates as `tabsFor`. */
export const environmentsFor = ({ isAgent, isSuperAdmin = true } = {}) =>
    ENVIRONMENTS.filter(
        (e) => !(e.adminOnly && isAgent) && !(e.superAdminOnly && !isSuperAdmin)
    );

/** First tab a user can actually see in an environment. */
export const defaultTabFor = (env, { isAgent, isSuperAdmin = true } = {}) =>
    tabsFor(env, { isAgent, isSuperAdmin })[0]?.id;
